import { apiClient } from './api-client';
import { OrganizationSettings } from '@scrambl/shared';
import { createLogger } from '@/lib/logger';

const logger = createLogger('lib/api/organization-settings.api');

export type BudgetOwnershipMode = 'PROJECT' | 'CLIENT'

export type OrganizationSettingsResponse = {
  settings: OrganizationSettings | null
  budgetOwnershipMode: BudgetOwnershipMode
  defaultMaxProjectCount: number | null
}

export const organizationSettingsApi = {
  getSettings: async (organizationId: string): Promise<OrganizationSettingsResponse> => {
    try {
      const response = await apiClient<{
        settings: any
        budgetOwnershipMode?: BudgetOwnershipMode
        defaultMaxProjectCount?: number | null
      }>(
        `/organizations/${organizationId}/settings`,
      )
      return {
        settings: response.settings || null,
        budgetOwnershipMode: response.budgetOwnershipMode ?? 'PROJECT',
        defaultMaxProjectCount: response.defaultMaxProjectCount ?? null,
      }
    } catch (error) {
      logger.error('[OrganizationSettingsApi] Error fetching settings:', error)
      throw error
    }
  },

  updateSettings: async (
    organizationId: string,
    settings: Partial<OrganizationSettings>,
    options?: {
      regeneratePayPeriods?: boolean
      budgetOwnershipMode?: BudgetOwnershipMode
      defaultMaxProjectCount?: number | null
    },
  ): Promise<OrganizationSettingsResponse> => {
    const response = await apiClient<{
      settings: OrganizationSettings
      budgetOwnershipMode?: BudgetOwnershipMode
      defaultMaxProjectCount?: number | null
    }>(
      `/organizations/${organizationId}/settings`,
      {
        method: 'PUT',
        body: {
          ...settings,
          regeneratePayPeriods: options?.regeneratePayPeriods,
          ...(options?.budgetOwnershipMode !== undefined && {
            budgetOwnershipMode: options.budgetOwnershipMode,
          }),
          ...(options?.defaultMaxProjectCount !== undefined && {
            defaultMaxProjectCount: options.defaultMaxProjectCount,
          }),
        },
      },
    )
    return {
      settings: response.settings,
      budgetOwnershipMode: response.budgetOwnershipMode ?? 'PROJECT',
      defaultMaxProjectCount: response.defaultMaxProjectCount ?? null,
    }
  },

  uploadPortalLogo: async (
    organizationId: string,
    file: File,
  ): Promise<{ logoUrl: string }> => {
    const formData = new FormData()
    formData.append('file', file, file.name)
    return apiClient<{ logoUrl: string }>(
      `/organizations/${organizationId}/client-portal/logo`,
      { method: 'POST', body: formData },
    )
  },

  uploadPortalCover: async (
    organizationId: string,
    file: File,
  ): Promise<{ coverImageUrl: string }> => {
    const formData = new FormData()
    formData.append('file', file, file.name)
    return apiClient<{ coverImageUrl: string }>(
      `/organizations/${organizationId}/client-portal/cover`,
      { method: 'POST', body: formData },
    )
  },
}
